import * as i0 from '@angular/core';
import { input, signal, computed, effect, Directive, NgModule } from '@angular/core';
import { cn, equals } from '@primeuix/utils';

/**
 * Bind directive provides dynamic attribute, property, and event listener binding functionality.
 * @group Components
 */
let Bind = /*#__PURE__*/(() => {
  class Bind {
    el;
    renderer;
    /**
     * Dynamic attributes, properties, and event listeners to be applied to the host element.
     * @group Props
     */
    pBind = input(undefined, ...(ngDevMode ? [{
      debugName: "pBind"
    }] : []));
    _attrs = signal(undefined, ...(ngDevMode ? [{
      debugName: "_attrs"
    }] : []));
    attrs = computed(() => this._attrs() || this.pBind(), ...(ngDevMode ? [{
      debugName: "attrs"
    }] : []));
    styles = computed(() => this.attrs()?.style, ...(ngDevMode ? [{
      debugName: "styles"
    }] : []));
    classes = computed(() => cn(this.attrs()?.class), ...(ngDevMode ? [{
      debugName: "classes"
    }] : []));
    listeners = [];
    constructor(el, renderer) {
      this.el = el;
      this.renderer = renderer;
      effect(() => {
        const {
          style,
          class: className,
          ...rest
        } = this.attrs() || {};
        for (const [key, value] of Object.entries(rest)) {
          if (key.startsWith('on') && typeof value === 'function') {
            const eventName = key.slice(2).toLowerCase();
            // add listener if not already added
            if (!this.listeners.some(l => l.eventName === eventName)) {
              const unlisten = this.renderer.listen(this.el.nativeElement, eventName, value);
              this.listeners.push({
                eventName,
                unlisten
              });
            }
          } else if (value === null || value === undefined) {
            // remove attr
            this.renderer.removeAttribute(this.el.nativeElement, key);
          } else {
            // attr & prop fallback
            this.renderer.setAttribute(this.el.nativeElement, key, value.toString());
            if (key in this.el.nativeElement) {
              this.el.nativeElement[key] = value;
            }
          }
        }
      });
    }
    ngOnDestroy() {
      this.clearListeners();
    }
    setAttrs(attrs) {
      if (!equals(this._attrs(), attrs)) {
        this._attrs.set(attrs);
      }
    }
    clearListeners() {
      this.listeners.forEach(({
        unlisten
      }) => unlisten());
      this.listeners = [];
    }
    static ɵfac = function Bind_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || Bind)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2));
    };
    static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: Bind,
      selectors: [["", "pBind", ""]],
      hostVars: 4,
      hostBindings: function Bind_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵstyleMap(ctx.styles());
          i0.ɵɵclassMap(ctx.classes());
        }
      },
      inputs: {
        pBind: [1, "pBind"]
      }
    });
  }
  return Bind;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let BindModule = /*#__PURE__*/(() => {
  class BindModule {
    static ɵfac = function BindModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || BindModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: BindModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
  return BindModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { Bind, BindModule };
